import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ActionType,
  WorkflowActionType,
} from '../../../../../core/models/action.model';
import { Notification } from '../../../../../core/models/notification.model';
import {
  EndState,
  WorkflowConfig,
  WorkflowState,
} from '../../../../../core/models/workflow.model';

const NOTIFICATION_ACTION_TYPE = 'NOTIFICATION';

@Component({
  selector: 'irembogov-notification-configuration',
  templateUrl: './notification-configuration.component.html',
  styleUrls: ['./notification-configuration.component.scss'],
})
export class NotificationConfigurationComponent implements OnInit, OnChanges {
  @Input() changeRequestJsonWorkflowData!: Record<string, unknown>;
  @Input() formFields!: any;
  @Output() workflowUpdated = new EventEmitter<any>();

  showForm = false;
  workflowConfig!: WorkflowConfig;
  notifications: Notification[] = [];
  selectedNotification: Notification | null = null;
  selectedState: WorkflowState | null = null;

  ngOnInit() {
    this.parseExistingNotifications();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['changeRequestJsonWorkflowData'] &&
      !changes['changeRequestJsonWorkflowData'].isFirstChange()
    ) {
      this.changeRequestJsonWorkflowData =
        changes['changeRequestJsonWorkflowData'].currentValue;

      // Re-parse the assignments to reflect the updated data
      this.parseExistingNotifications();
    }
  }

  parseExistingNotifications() {
    if (this.changeRequestJsonWorkflowData) {
      this.workflowConfig = {
        workflow:
          (this.changeRequestJsonWorkflowData['workflow'] as any[]) || [],
        nextSteps:
          (this.changeRequestJsonWorkflowData['nextSteps'] as any[]) || [],
        expectedProcessingTime:
          (this.changeRequestJsonWorkflowData[
            'expectedProcessingTime'
          ] as number) || 0,
        expectedProcessingMeasurementUnit:
          (this.changeRequestJsonWorkflowData[
            'expectedProcessingMeasurementUnit'
          ] as string) || '',
      };

      this.notifications = this.workflowConfig.workflow
        .map(step => this.extractNotifications(step))
        .filter(
          (notification): notification is Notification => notification !== null
        );
    }
  }

  private findNotificationAction(endState: any): any | null {
    if (!endState) return null;

    if (endState.breakingAction?.actionType === NOTIFICATION_ACTION_TYPE) {
      return endState.breakingAction;
    }

    const breakingAction = endState.breakingActions?.find(
      (action: any) => action.actionType === NOTIFICATION_ACTION_TYPE
    );
    if (breakingAction) return breakingAction;

    const nonBreakingAction = endState.nonBreakingActions?.find(
      (action: any) => action.actionType === NOTIFICATION_ACTION_TYPE
    );
    if (nonBreakingAction) return nonBreakingAction;

    return null;
  }

  private determineActionType(step: any, endStateKey: string): ActionType {
    const endState = step[endStateKey];
    const hasOfficeAssignment = this.findNotificationAction(endState);

    if (!hasOfficeAssignment) return WorkflowActionType.BREAKING_ACTION;

    if (endState.breakingAction?.actionType === NOTIFICATION_ACTION_TYPE) {
      return WorkflowActionType.BREAKING_ACTION;
    }
    if (
      endState.breakingActions?.some(
        (action: any) => action.actionType === NOTIFICATION_ACTION_TYPE
      )
    ) {
      return WorkflowActionType.BREAKING_ACTIONS;
    }
    if (
      endState.nonBreakingActions?.some(
        (action: any) => action.actionType === NOTIFICATION_ACTION_TYPE
      )
    ) {
      return WorkflowActionType.NON_BREAKING_ACTIONS;
    }

    return WorkflowActionType.BREAKING_ACTION;
  }

  extractNotifications(step: any): Notification | null {
    // Check both end states for notification action
    const endStateOneConfig = this.findNotificationAction(step.endStateOne);
    const endStateTwoConfig = this.findNotificationAction(step.endStateTwo);

    if (!endStateOneConfig && !endStateTwoConfig) {
      return null;
    }

    const endStateType = endStateOneConfig
      ? EndState.END_STATE_ONE
      : EndState.END_STATE_TWO;
    const endState = step[endStateType];
    const actionType = this.determineActionType(step, endStateType);
    const configAction = this.findNotificationAction(endState);

    if (!configAction) {
      return null;
    }

    return {
      workflowContext: {
        startState: step.startState,
        event: step.event,
        endStateType,
        endStateCode: endState.stateCode,
        actionType,
        displayName: step.eventNames,
      },
      type: configAction.args.notificationType,
      staticTemplate: configAction.args.staticTemplate,
      dynamicTemplate: configAction.args.dynamicTemplate,
    };
  }

  onAddNew() {
    this.showForm = true;
  }

  onCancel() {
    this.showForm = false;
    this.selectedNotification = null;
    this.selectedState = null;
  }

  onStateSelected(state: WorkflowState) {
    if (state && state.startState && state.event && state.endStateType) {
      this.selectedState = state;
      const existingNotification = this.getCurrentFormNotification();
      if (existingNotification) {
        this.selectedNotification = existingNotification;
      } else {
        this.selectedNotification = null;
      }
    }
  }

  getCurrentFormNotification(): Notification | null {
    if (this.selectedNotification) {
      return this.selectedNotification;
    }
    if (!this.selectedState) {
      return null;
    }
    return (
      this.notifications.find(
        a =>
          a.workflowContext.startState === this.selectedState?.startState &&
          a.workflowContext.event === this.selectedState?.event &&
          a.workflowContext.endStateType === this.selectedState?.endStateType
      ) || null
    );
  }

  getWorkflowState(): WorkflowState {
    return (
      this.selectedState || {
        startState: '',
        event: '',
        endStateType: 'endStateOne',
        endStateCode: '',
        actionType: 'breakingAction',
      }
    );
  }

  onNotificationChange(notification: Notification) {
    if (this.selectedNotification) {
      // If updating existing notification
      const oldEndStateType =
        this.selectedNotification.workflowContext.endStateType;
      const newEndStateType = notification.workflowContext.endStateType;

      // Find the step that contains this notification
      const step = this.workflowConfig.workflow.find(
        s =>
          s.startState === notification.workflowContext.startState &&
          s.event === notification.workflowContext.event
      );

      if (step) {
        // If moving between end states or changing action type
        if (
          oldEndStateType !== newEndStateType ||
          this.selectedNotification.workflowContext.actionType !==
            notification.workflowContext.actionType
        ) {
          // Remove from old location
          this.removeNotificationFromEndState(step[oldEndStateType]);
        }
      }

      // Update notifications list
      this.notifications = this.notifications.map(a =>
        this.selectedNotification &&
        this.isSameNotification(a, this.selectedNotification)
          ? notification
          : a
      );
    } else {
      // New notification
      this.notifications = [...this.notifications, notification];
    }

    this.updateWorkflowConfigFromNotifications();
    this.showForm = false;
    this.selectedNotification = null;
    this.selectedState = null;
  }

  private removeNotificationFromEndState(endState: any) {
    if (endState.breakingAction?.actionType === NOTIFICATION_ACTION_TYPE) {
      endState.breakingAction = null;
    }

    if (Array.isArray(endState.breakingActions)) {
      endState.breakingActions = endState.breakingActions.filter(
        (action: any) => action.actionType !== NOTIFICATION_ACTION_TYPE
      );
      if (endState.breakingActions.length === 0) {
        endState.breakingActions = null;
      }
    }

    if (Array.isArray(endState.nonBreakingActions)) {
      endState.nonBreakingActions = endState.nonBreakingActions.filter(
        (action: any) => action.actionType !== NOTIFICATION_ACTION_TYPE
      );
      if (endState.nonBreakingActions.length === 0) {
        endState.nonBreakingActions = null;
      }
    }
  }

  private isSameNotification(a1: Notification, a2: Notification): boolean {
    return (
      a1.workflowContext.startState === a2.workflowContext.startState &&
      a1.workflowContext.event === a2.workflowContext.event &&
      a1.workflowContext.endStateType === a2.workflowContext.endStateType
    );
  }

  private updateWorkflowConfigFromNotifications() {
    const updatedWorkflow = this.workflowConfig.workflow.map(step => {
      const matchingNotification = this.notifications.find(
        a =>
          a.workflowContext.startState === step.startState &&
          a.workflowContext.event === step.event
      );

      if (matchingNotification) {
        const endStateKey = matchingNotification.workflowContext.endStateType;
        const updatedEndState = this.updateEndStateActions(
          step[endStateKey] || {},
          matchingNotification
        );

        return {
          ...step,
          [endStateKey]: updatedEndState,
        };
      }
      return step;
    });

    this.workflowUpdated.emit(updatedWorkflow);
  }

  private updateEndStateActions(
    currentEndState: any,
    notification: Notification
  ) {
    // Create a deep copy to avoid modifying the original
    const updatedEndState = JSON.parse(JSON.stringify(currentEndState));

    // First, remove any existing notification from all possible locations
    this.removeNotificationFromEndState(updatedEndState);

    // Create the new notification action
    const notificationAction = {
      actionType: NOTIFICATION_ACTION_TYPE,
      args: this.createNotificationArgs(notification),
    };

    // Add the notification action to the appropriate location
    switch (notification.workflowContext.actionType) {
      case WorkflowActionType.BREAKING_ACTION:
        updatedEndState.breakingAction = notificationAction;
        break;

      case WorkflowActionType.BREAKING_ACTIONS:
        if (!Array.isArray(updatedEndState.breakingActions)) {
          updatedEndState.breakingActions = [];
        }
        updatedEndState.breakingActions.push(notificationAction);
        break;

      case WorkflowActionType.NON_BREAKING_ACTIONS:
        if (!Array.isArray(updatedEndState.nonBreakingActions)) {
          updatedEndState.nonBreakingActions = [];
        }
        updatedEndState.nonBreakingActions.push(notificationAction);
        break;
    }

    return updatedEndState;
  }

  private createNotificationArgs(notification: Notification): any {
    return {
      notificationType: notification.type,
      staticTemplate: notification.staticTemplate,
      dynamicTemplate: notification.dynamicTemplate,
    };
  }

  onEdit(notification: Notification) {
    this.selectedNotification = notification;
    this.selectedState = notification.workflowContext;
    this.showForm = true;
  }

  onDelete(notification: Notification) {
    if (confirm('Are you sure you want to delete this notification?')) {
      // Find the corresponding step and end state
      const step = this.workflowConfig.workflow.find(
        s =>
          s.startState === notification.workflowContext.startState &&
          s.event === notification.workflowContext.event
      );

      if (step) {
        const endState = step[notification.workflowContext.endStateType];
        // Remove notification action while preserving other actions
        this.removeNotificationFromEndState(endState);

        // Update notifications list
        this.notifications = this.notifications.filter(
          a => !this.isSameNotification(a, notification)
        );
      }

      this.updateWorkflowConfigFromNotifications();
    }
  }
}
